'use client';

import { useSelector } from 'react-redux';
import { Col, Container, Row } from '@geberit/gdds';
import { usePathname } from 'next/navigation';

// styles
import styles from './paymentMethods.module.scss';

// utils
import { isEmpty } from 'utils/is-empty';
import { useXy } from 'utils/hooks/use-xy';
import { useGroup } from 'utils/hooks/use-group';
import { buildSize, gridSizes } from 'utils/gridSize';
import {
  webshopLandingpageUrlSelector,
  webshopPaymentMethodSelector,
} from 'utils/selectors/globalsSelectors';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { getPaymentLogo, sortPaymentMethods } from 'utils/get-payment-logo';

type PaymentMethodsProps = {
  forceShow?: boolean;
};

export default function PaymentMethods({ forceShow = false }: Readonly<PaymentMethodsProps>) {
  const isGroup = useGroup();
  const isXy = useXy();
  const webshopUrl = useSelector(webshopLandingpageUrlSelector);
  const pathname = usePathname();
  const paymentMethod = useSelector(webshopPaymentMethodSelector);
  const sortedMethods = sortPaymentMethods(paymentMethod);
  const translate = useTranslationFunction();

  const renderPaymentLogo = ({ translationKey, alt }) => {
    const fileName = getPaymentLogo(translationKey);
    if (!fileName) return null;
    return <img src={`${fileName}`} alt={alt} className={styles.entryImg} />;
  };

  // No payment methods - or it is group don't show.
  if (isEmpty(sortedMethods) || isGroup) return null;

  // If the page belongs to the webshop, display it - or show it if forced.
  const show = !isEmpty(webshopUrl) && pathname.startsWith(webshopUrl);
  if (!show && !forceShow) return null;

  return (
    <div className={styles.component}>
      <Container
        maxContentWidth={isXy ? buildSize(gridSizes.gddsFullGrid) : '80rem'}
        className={styles.content}
      >
        <Row>
          <Col>
            <div className={styles.entries}>
              {sortedMethods.map((translationKey, index) => (
                <div
                  key={`${translationKey}-${index}`}
                  className={styles.entry}
                  aria-label={translate(translationKey)}
                  title={translate(translationKey)}
                >
                  {renderPaymentLogo({ translationKey, alt: translate(translationKey) })}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
